/* eslint-disable waimaie/no-direct-access-localstorage */
import { Observable, throwError, timer, MonoTypeOperatorFunction, Subject } from 'rxjs';
import { retryWhen, mergeMap, finalize } from 'rxjs/operators';
import moment from 'moment';

import { reportCustomMetrics, CUSTOM_METRICS, reportCustomError } from '@utils/owlReporter';
import request from '@utils/axiosWrapper';
import * as Ids from '@utils/ids';
import { setCookie, delCookie, getCookie } from '@utiljs/cookie';
import { ENV, isInApp, isInOldApp4, isWindowsXP } from '@utils/getEnv';
import { removeStorage, setStorage, getStorage } from '@utils/localStorage';
import { db } from '@utils/wmIndexDB';
import Horn from '@utils/horn';
import { isHitGray, ICommoneHornGrayConfig } from '@utils/commonHornGrayUtils';
import { requestGrayInfo } from '@utils/grayAB';
import { failToast } from '@utils/toast';
import set from '@utils/set';
import * as LLog from '@utils/llogWrapper';
import * as LX from '@utils/lxWrapper';

import bellService from './service/bell';
import loginStateMonitor from './service/loginStateMonitor';
import pcClientApiMonitor from './service/pcClientApiMonitor';

import printTemplateService from './service/printTemplate';
import checkWebp from './utils/checkWebp';
import { IAllPoiList } from './api/define';
import poiInfoCache from './utils/poiInfoCache';
import { updateProxyTokenInUrlFixWhiteList } from './utils/goPager';
import store from './store';
import checkJumpToSC from './modules/JumpToSC';
import { AjaxType, DemotionConfigEntry } from './store/models/common';
import doLogout from './utils/doLogout';

const bootStateSubject = new Subject<string>();

type HomePageBasicInfo = {
    /** 当前环境 */
    env: ENV;
    /** 当前环境的proxy域名，用于拼接地址 */
    eappBaseUrl: string;
    /** 当前门店名称 */
    wmPoiName: string;
    /**
     * 当前账号是否关联了多个门店
     * 0：当前账号只绑定了1个门店（200账号 or 300账号但是只有1个门店）
     * 1：300账号且绑定了多个门店
     * */
    isChain: 0 | 1;
    /** 当前门店营业状态描述文案 */
    wmPoiStatusDesc: string;
    /** 当前账号名称 */
    acctName: string;
    /** 当前账号是否绑定手机 */
    hasBoundPhone: string;
    /** 当前账号在当前设备上开启了自动接单的门店 */
    autoAcceptWmPoiList: string;
    /** push多域名容灾灰度百分比 */
    pushPercentage: number;

    csrfToken: string;
    signToken: string;
    isSgCate: 0 | 1;

    ignoreSetRouterProxy: boolean;
    regionId: string;
    regionVersion: number;
    /** 兼容PC大连锁切换到单门店走Set的regionId */
    regionIdForSingle: string | number;
    /** 兼容PC大连锁切换到单门店走Set的regionVersion */
    regionVersionForSingle: string | number;
    /** 挂牌轮询配置 */
    demotionConfig: DemotionConfigEntry[];
    /** 是否是闪购或医药 */
    scIndex: number;
    /** token */
    token: string;
};

/** 请求登录主接口 */
const getHomePageBasicInfo = (): Promise<HomePageBasicInfo> => {
    return request
        .rawGet<{ code: number; data: HomePageBasicInfo; msg: string }>(
            '/api/v2/account/homePage',
            {
                params: { acctId: Ids.getAcctId() },
            },
        )
        .then(res => {
            reportCustomMetrics(CUSTOM_METRICS.HOMEPAGE_API.KEY, 1, {
                [CUSTOM_METRICS.HOMEPAGE_API.TAGS.HTTP_SUCCESS]: 1,
                [CUSTOM_METRICS.HOMEPAGE_API.TAGS.BUSINESS_CODE]: res.code,
            });
            if (res.code === 0) {
                return res.data;
            } else if (res.code === 1001) {
                // 未登录，跳转登录页
                doLogout();
                throw new Error(`Code: ${res.code}, Msg: ${res.msg}`);
            } else if (res.code === 1017) {
                // 账号门店绑定关系异常，强制退登
                doLogout();
                throw new Error(`Code: ${res.code}, Msg: ${res.msg}`);
            } else {
                // 其它业务报错
                throw new Error(`Code: ${res.code}, Msg: ${res.msg}`);
            }
        })
        .catch(error => {
            reportCustomMetrics(CUSTOM_METRICS.HOMEPAGE_API.KEY, 1, {
                [CUSTOM_METRICS.HOMEPAGE_API.TAGS.HTTP_SUCCESS]: 0,
            });
            throw new Error(`Msg: ${error}`);
        });
};

/** 通用的重试策略 */
const genericRetryStrategy = ({
    maxRetryAttempts = 3,
    scalingDuration = 2000,
} = {}): MonoTypeOperatorFunction<any> => {
    return errors =>
        errors.pipe(
            mergeMap((error, index) => {
                const retryAttempt = index + 1;
                if (retryAttempt > maxRetryAttempts) {
                    return throwError(error);
                }
                console.log(
                    `尝试第 ${retryAttempt} 次重试: ${retryAttempt * scalingDuration}ms 后执行`,
                );
                return timer(retryAttempt * scalingDuration);
            }),
            finalize(() => console.log('都结束了!')),
        );
};

/** 请求基础信息 */
const prepareHomePageBasicInfo = () => {
    const getHomePageBasicInfo$ = new Observable<HomePageBasicInfo>(subscriber => {
        getHomePageBasicInfo().then(
            res => subscriber.next(res),
            err => subscriber.error(err),
        );
    }).pipe(retryWhen(genericRetryStrategy())); // 主接口会重试 3 次

    return new Promise<HomePageBasicInfo>((resolve, reject) => {
        getHomePageBasicInfo$.subscribe(
            res => resolve(res),
            err => reject(err),
        );
    }).then(res => {
        // 把接口里取到的数据 polyfill 到 window/localStorage 中去，保持老架构一致性

        // localStorage
        localStorage.setItem('isChain', `${res.isChain}`);
        localStorage.setItem('autoAcceptWmPoiList', res.autoAcceptWmPoiList);

        /** 兼容PC大连锁切换到单门店走Set
         * https://km.sankuai.com/collabpage/2234376459
         */
        if (res.regionIdForSingle && res.regionVersionForSingle) {
            setCookie(
                'set_info_single',
                JSON.stringify({
                    regionIdForSingle: res.regionIdForSingle,
                    regionVersionForSingle: res.regionVersionForSingle,
                }),
                {
                    expires: new Date(new Date().getTime() + 24 * 30 * 60 * 60 * 1000),
                },
            );
        } else {
            delCookie('set_info_single');
        }

        // window
        window.isChain = res.isChain;
        window.wamai_e_acct_name = res.acctName;
        window.CommConstants = {
            ENV: res.env,
            eappBaseUrl: res.eappBaseUrl,
            IS_CHAIN: res.isChain === 1,
            IS_IN_APP: !!window.wmSystemApi,
        };
        window.pushPercentage = res.pushPercentage;
        window.scIndex = res.scIndex;
        // store 数据初始化
        const {
            isChain,
            wmPoiName,
            wmPoiStatusDesc,
            acctName,
            hasBoundPhone,
            demotionConfig = [],
        } = res;
        store.dispatch({
            type: 'shellCommon/setData',
            payload: {
                isChain,
                wmPoiName,
                wmPoiStatusDesc,
                acctName,
                hasBoundPhone,
                demotionConfig,
            },
        });
        /** 更新 set 化 */
        set.setParams({
            wmPoiId: Ids.getWmPoiId(),
            ignoreSetRouterProxy: res.ignoreSetRouterProxy,
            region_id: res.regionId,
            region_version: res.regionVersion,
        });
        return res;
    });
};

/** 请求 Horn 配置 */
const prepareHornConfig = () => {
    const getHornConfig$ = new Observable<any>(subscriber => {
        Horn.loadHornConfigFromServer().then(
            res => subscriber.next(res),
            err => subscriber.error(err),
        );
    }).pipe(retryWhen(genericRetryStrategy({ maxRetryAttempts: 1 })));

    // Horn 重试 1 次，允许失败
    return new Promise(resolve => {
        getHornConfig$.subscribe(
            () => resolve(),
            () => resolve(), // 允许失败，尝试2次，尽力了
        );
    });
};

/** 请求门店列表 */
const preparePoiList = () => {
    const getPoiList$ = new Observable<IAllPoiList[]>(subscriber => {
        request.postAsFormSubmit('/api/poi/poiList').then(
            res => subscriber.next(res),
            err => subscriber.error(err),
        );
    }).pipe(retryWhen(genericRetryStrategy({ maxRetryAttempts: 1 })));

    // 重试一次，门店列表存入IndexDB
    return new Promise<IAllPoiList[]>((resolve, reject) => {
        getPoiList$.subscribe(
            res => resolve(res),
            err => reject(err),
        );
    }).then(
        res => {
            try {
                window.localStorage.setItem(
                    'localALLPoiIds',
                    JSON.stringify(res.map((item: IAllPoiList) => item.id)),
                );
                // TODO: 暂存门店列表数据
                window.localStorage.setItem('localAllPoiList', JSON.stringify(res));
            } catch (e) {
                // FIXME: TODO: 待localAllPoiList使用方全部更新读取逻辑后，优化
                // NOTE: 过度阶段同时写入localALLPoiIds和localAllPoiList会增加localStorage写爆的风险
                // NOTE: 此处主动catch，防止影响后续IndexedDB写入进而影响门店下拉列表的展示。
            }
            // 团餐相关逻辑
            if (Ids.getWmPoiId() !== '-1') {
                const currentPoi = res.find(item => {
                    return item.id === Number(Ids.getWmPoiId());
                });
                if (currentPoi && currentPoi.groupMealPoi === 1) {
                    setCookie('groupMealPoi', '1');
                } else {
                    delCookie('groupMealPoi');
                }
            }

            // 重置本地门店列表缓存数据
            return db.poiList.clear().then(() => {
                return db.poiList.bulkAdd(res).then(() => {
                    // 在本地记录当前缓存的门店信息对应的账号ID（确保IndexedDB写入成功后在更新缓存）
                    setStorage('LOCAL_POI_LIST_CACHE_ACCOUNT', Ids.getAcctId());
                    return res;
                });
            });
        },
        error => {
            /** 存储门店列表请求状态，用于门店列表展示异常上报 */
            store.dispatch({
                type: 'shellCommon/setData',
                payload: {
                    poilistAjaxType: AjaxType.REJECT,
                },
            });
            throw new Error(error);
        },
    );
};

/** 请求 AB 灰度配置 */
const prepareGrayABConfig = () => {
    const getGrayABConfig$ = new Observable<any>(subscriber => {
        requestGrayInfo().then(
            () => subscriber.next(),
            () => subscriber.error(),
        );
    }).pipe(retryWhen(genericRetryStrategy({ maxRetryAttempts: 1 })));

    // AB灰度 重试 1 次，允许失败
    return new Promise(resolve => {
        getGrayABConfig$.subscribe(
            () => resolve(),
            () => resolve(), // 允许失败，尝试2次，尽力了
        );
    });
};

/** 定制化配置 XBK在用 */
const customizedShow = () => {
    const getcCustomizedShow$ = new Observable<[]>(subscriber => {
        request.get('/gw/api/account/customized/show').then(
            res => subscriber.next(res),
            err => subscriber.error(err),
        );
    }).pipe(retryWhen(genericRetryStrategy({ maxRetryAttempts: 1 })));
    return new Promise<string[]>(resolve => {
        getcCustomizedShow$.subscribe(
            res => resolve(res || []),
            () => resolve([]),
        );
    }).then(res => {
        store.dispatch({
            type: 'shellCommon/setData',
            payload: {
                customizedShowArr: res || [],
            },
        });
        setStorage('customizedShow', res.join(','));
    });
};

/** 给Proxy域名预种cookie */
const syncCookieToProxyDomain = (token: string) => {
    const syncCookieToProxy$ = new Observable<string[]>(subscriber => {
        request
            .postAsFormSubmit<{ grayStaticPageRouteList: string[] }>(
                `${window.CommConstants.eappBaseUrl}/pc_to_proxy_write_cookie_bridge`,
                {
                    wmPoiId: Ids.getWmPoiId(),
                    acctId: Ids.getAcctId(),
                    token: token || Ids.getToken(),
                    bsid: getCookie('bsid'),
                    appType: 3,
                },
                {
                    withCredentials: true,
                },
            )
            .then(
                res =>
                    subscriber.next(
                        Array.isArray(res.grayStaticPageRouteList)
                            ? res.grayStaticPageRouteList
                            : [],
                    ),
                err => subscriber.error(err),
            );
    }).pipe(retryWhen(genericRetryStrategy({ maxRetryAttempts: 1 })));
    return new Promise<string[]>(resolve => {
        syncCookieToProxy$.subscribe(
            grayStaticPageRouteList => resolve(grayStaticPageRouteList),
            () => resolve([]),
        );
    }).then(grayStaticPageRouteList => {
        // 拿到通参需要移除token字段的proxy路由白名单
        updateProxyTokenInUrlFixWhiteList(grayStaticPageRouteList);
    });
};

/**
 * 拉取启动商家中心壳子页面的基础信息
 *
 * 在此函数中运行的代码只能访问商家刚刚登录后的有限信息：acctId, wmPoiId, token 等。
 */
const boot = () => {
    bellService.registerBuiltinBells();

    // 浏览器是否支持 webp
    checkWebp();

    // 切换门店或者刷新外层页面之后，清空打印配置信息（打印的时候重拉接口获取即可）
    localStorage.removeItem('waimai_e_print_config');
    // 清空广告信息（打印的时候重拉接口获取）
    localStorage.removeItem('waimai_e_print_ad_info');

    /**
     * 优先请求主接口以及不需要登录态的 Horn 配置
     *
     * 主接口不允许失败，其他接口均允许失败（失败后会使用本地缓存数据兜底）
     */

    return Promise.all([prepareHomePageBasicInfo(), prepareHornConfig()]).then(
        async ([homePageRes]) => {
            /** 判断是否命中 PC 客户端升级强制引导弹框以及是否需要强制踢登 */
            let hitForceLogout = false;
            const pcClientLoginUpdateGrayConfig = Horn.getConfigByFieldName<{
                loginUpdateForceDialog?: ICommoneHornGrayConfig;
            }>(Horn.CONFIG_FIELD.PC_CLIENT_LOGIN_UPDATE_GRAY);
            if (
                !isWindowsXP &&
                isInOldApp4 &&
                isHitGray(
                    (pcClientLoginUpdateGrayConfig &&
                        pcClientLoginUpdateGrayConfig.loginUpdateForceDialog) ||
                        null,
                )
            ) {
                // 命中灰度，继续判断当天是否已经展示过升级弹框
                const pcClientV4ForceUpdateStr = localStorage.getItem('pcClientV4ForceUpdate');
                let pcClientV4ForceUpdate: { lastShowDate?: string; leftDay?: number } = {};
                try {
                    pcClientV4ForceUpdate = JSON.parse(pcClientV4ForceUpdateStr || '{}') || {};
                } catch (e) {
                    // something bad happens~
                }

                if (pcClientV4ForceUpdate.lastShowDate !== moment().format('YYYYMMDD')) {
                    // 今天还没有展示过引导弹框，退出登录去登录页看一遍
                    reportCustomMetrics(CUSTOM_METRICS.CLIENTV4_FORCE_UPGRADE_LOGOUT.KEY, 1, {
                        [CUSTOM_METRICS.CLIENTV4_FORCE_UPGRADE_LOGOUT.TAGS
                            .ACCT_ID]: Ids.getAcctId(),
                        [CUSTOM_METRICS.CLIENTV4_FORCE_UPGRADE_LOGOUT.TAGS
                            .DEVICE_UUID]: Ids.getDeviceId(),
                    });
                    LX.reportMV('b_waimai_e_vbkiirn5_mv', 'c_waimai_e_fa4ps4ng', {
                        [CUSTOM_METRICS.CLIENTV4_FORCE_UPGRADE_LOGOUT.TAGS
                            .ACCT_ID]: Ids.getAcctId(),
                        [CUSTOM_METRICS.CLIENTV4_FORCE_UPGRADE_LOGOUT.TAGS
                            .DEVICE_UUID]: Ids.getDeviceId(),
                    });
                    hitForceLogout = true;
                    doLogout();
                }
            }

            if (getCookie('virtual') === '1') {
                // BD虚拟登录
                window.location.href = '/virtual';
            }
            try {
                /** 外层页面初始化上报llog */
                LLog.initLLog('shell');
            } catch (error) {
                // console.log('')
            }

            /** 闪购账号跳转闪购商家中心 */
            if (!hitForceLogout) {
                if (window.scIndex !== 0) {
                    await checkJumpToSC(homePageRes.token);
                } else {
                    if (window.wmSystemApi && window.wmSystemApi.setBusinessLine) {
                        window.wmSystemApi.setBusinessLine('waimai', {
                            acctId: Ids.getAcctId(),
                        });
                    }
                }
            }

            // 请求左侧菜单
            store.dispatch({ type: 'shellLeftMenu/getLeftMenuList' });

            // 如果切换过账号，先把本地缓存的门店列表删除
            // 如果切换过账号，清空消息设置提醒Tips
            if (getStorage('LOCAL_POI_LIST_CACHE_ACCOUNT') !== Ids.getAcctId()) {
                db.poiList.clear();
                window.localStorage.removeItem('localALLPoiIds');
                // TODO: 暂存门店列表数据
                window.localStorage.removeItem('localAllPoiList');
                removeStorage('MESSAGE_SETTING_TIPS');
            }

            /** 拼好饭灰度请求 */
            // requestPHFGrayInfo();

            const changeStorePoiListAjaxType = () => {
                /** 存储门店列表请求状态，用于门店列表展示异常上报，此时接口请求+后续的清除db和写入 db 动作完成，不管db操作成功还是失败 */
                store.dispatch({
                    type: 'shellCommon/setData',
                    payload: {
                        poilistAjaxType: AjaxType.RESOVLE,
                    },
                });
            };

            // 请求门店列表，之后请求门店小票模板
            preparePoiList().then(
                res => {
                    changeStorePoiListAjaxType();
                    if (isInApp) {
                        // 仅在PC客户端内加载小票模板
                        printTemplateService.initTempalteData();
                    }
                    poiInfoCache.setCache(res);
                    try {
                        // 检测当前门店是否自入驻，更新cookie状态（仅单店）
                        if (Ids.getWmPoiId() !== '-1') {
                            const curPoi = res.find(item => item.id === Number(Ids.getWmPoiId()));
                            if (curPoi) {
                                setCookie('isOfflineSelfOpen', curPoi.status === 0 ? '1' : '0');
                            }
                        }
                    } catch (error) {
                        // 上报
                    }
                },
                e => {
                    changeStorePoiListAjaxType();
                    // 接口请求失败，判断本地缓存是否可用
                    if (getStorage('LOCAL_POI_LIST_CACHE_ACCOUNT') === Ids.getAcctId()) {
                        printTemplateService.initTempalteData();
                        db.poiList.toArray().then(res => {
                            poiInfoCache.setCache(res);
                        });
                    } else {
                        failToast('门店列表加载失败，请稍后重试');
                    }

                    if (e.status === 403) {
                        reportCustomError({
                            name: 'poiList接口被拦截',
                            message: `poiList接口被拦截-${e}`,
                            level: 'WARN',
                        });
                    } else {
                        reportCustomError({
                            name: 'db清除/写入poiList异常',
                            message: `db清除/写入poiList异常-${e}`,
                        });
                    }
                    /**
                     * 移除之前的向外抛出错误事件，采用自定义上报，都归为清除或者写入错误。避免多次上报影响可用性
                     */
                },
            );

            // 然后等【灰度】、【定制化展示接口】以及【Proxy种cookie】
            return Promise.all([
                prepareGrayABConfig(),
                customizedShow(),
                syncCookieToProxyDomain(homePageRes.token),
            ]).then(() => {
                // 周知监听方【基本信息✅  登录态✅  set化参数✅】
                bootStateSubject.next('readyToGo');
                // 开始登录态检测
                loginStateMonitor.startDetect();

                // 检查壳子中 wmSystemApi 丢失情况
                pcClientApiMonitor.detect();
            });
        },
    );
};

export default boot;

export { bootStateSubject };
